/**
 * Shared behaviour for the Flux theme. Loaded with `defer` on every page.
 * Custom elements re-initialise themselves when the theme editor re-renders
 * a section, so no shopify:section:load handlers are needed, except in the
 * docs contents list, which reads other sections.
 */

/**
 * Muted, looping inline video. Above-the-fold videos carry the `autoplay`
 * attribute and start on load for every visitor, as on fluxkeyboard.com,
 * including those whose OS asks for reduced motion; the toggle button lets
 * anyone stop it. The rest start as they near the viewport, so the browser
 * doesn't download every clip on a page up front. All pause while scrolled
 * away and resume on return.
 */
class AutoVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.auto-video__toggle');
    if (!this.video) return;

    // Set as a property too, so the browser's autoplay check sees it in time
    this.video.muted = true;
    this.userPaused = false;

    this.video.addEventListener('play', () => this.sync());
    this.video.addEventListener('pause', () => this.sync());
    this.button?.addEventListener('click', () => this.toggle());

    // Covers browsers that ignore the attribute until the element is visible
    if (this.video.autoplay) this.video.play().catch(() => {});
    // Autoplay may have started before this script ran, so the play event was missed
    this.sync();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.userPaused) {
          // Blocked playback (e.g. data saver) leaves the first frame showing
          this.video.play().catch(() => {});
        } else if (!entry.isIntersecting) {
          this.video.pause();
        }
      },
      // Starts a clip a little before it scrolls into view, so it's already playing when seen
      { rootMargin: '25% 0px' }
    );
    this.observer.observe(this.video);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  toggle() {
    if (this.video.paused) {
      this.userPaused = false;
      this.video.play().catch(() => {});
    } else {
      // Once someone pauses a video, scrolling doesn't restart it
      this.userPaused = true;
      this.video.pause();
    }
  }

  sync() {
    const playing = !this.video.paused;
    this.toggleAttribute('playing', playing);
    if (this.button) {
      this.button.setAttribute(
        'aria-label',
        playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay
      );
    }
  }
}

if (!customElements.get('auto-video')) customElements.define('auto-video', AutoVideo);

/** Mobile menu: a <details> panel that closes when a link inside it is followed. */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.querySelectorAll('a').forEach((link) =>
      link.addEventListener('click', () => this.details?.removeAttribute('open'))
    );
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.details?.open) {
        this.details.removeAttribute('open');
        this.details.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}

if (!customElements.get('mobile-menu')) customElements.define('mobile-menu', MobileMenu);

/**
 * Opens the preferences dialog of Shopify's cookie banner (Settings > Customer
 * privacy). Falls back to the link's href, the cookie policy page, when the
 * banner isn't enabled.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-cookie-settings]');
  if (!trigger || !window.privacyBanner?.showPreferences) return;
  event.preventDefault();
  window.privacyBanner.showPreferences();
});

/** Updates every cart count badge, e.g. after an AJAX add to cart. */
window.FluxCart = {
  async refreshCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
      return cart;
    } catch {
      return null;
    }
  },
};

/**
 * Variant picker for standard product pages: option buttons select the
 * matching variant, then the price, availability and hidden variant input
 * follow it. Variant data comes from a JSON script inside the element.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    if (!data) return;
    this.variants = JSON.parse(data.textContent);
    this.form = document.getElementById(this.dataset.form);
    this.addEventListener('change', () => this.update());
  }

  update() {
    const selected = [...this.querySelectorAll('fieldset')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    const section = this.closest('.flux-product');

    if (this.form) {
      const input = this.form.querySelector('input[name="id"]');
      const button = this.form.querySelector('[type="submit"]');
      if (input && variant) input.value = variant.id;
      if (button) {
        button.disabled = !variant || !variant.available;
        button.textContent = !variant
          ? button.dataset.labelUnavailable
          : variant.available
            ? button.dataset.labelAdd
            : button.dataset.labelSoldOut;
      }
    }

    const price = section?.querySelector('[data-product-price]');
    if (price && variant) price.textContent = variant.price_formatted;

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

/**
 * Docs contents list, built from the chapter titles and subheadings (h2 and
 * h3 with an id) of the Docs chapter sections on the page. This is the one
 * element that reads other sections, so in the theme editor it rebuilds
 * whenever a section is re-rendered, added, removed or moved.
 */
class DocsToc extends HTMLElement {
  static editorEvents = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'];

  connectedCallback() {
    this.list = this.querySelector('ul');
    // Unload fires before the section leaves the page, so wait a frame
    this.rebuild = () => requestAnimationFrame(() => this.build());
    this.build();
    if (window.Shopify?.designMode) {
      DocsToc.editorEvents.forEach((name) => document.addEventListener(name, this.rebuild));
    }
  }

  disconnectedCallback() {
    DocsToc.editorEvents.forEach((name) => document.removeEventListener(name, this.rebuild));
  }

  build() {
    const headings = document.querySelectorAll('.flux-docs-chapter :is(h2, h3)[id]');
    this.list.replaceChildren(
      ...Array.from(headings, (heading) => {
        const item = document.createElement('li');
        if (heading.tagName === 'H3') item.className = 'flux-docs__toc-sub';
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        return item;
      })
    );
    this.hidden = headings.length === 0;
  }
}

if (!customElements.get('flux-docs-toc')) customElements.define('flux-docs-toc', DocsToc);

/**
 * Testimonials: places the posts row by row into as many columns as the CSS
 * asks for (--cols) without moving them in the DOM, so tab and reading order
 * follow the rows. Shows about two and a half rows behind a fade with a
 * "See more" button, and fills a visually hidden list of the shipped-to
 * countries, named in the page's language, for screen readers.
 */
class FluxTestimonials extends HTMLElement {
  connectedCallback() {
    this.fillCountryList();

    this.wall = this.querySelector('.flux-testimonials__wall');
    this.grid = this.querySelector('.flux-testimonials__grid');
    this.fade = this.querySelector('.flux-testimonials__fade');
    if (!this.wall || !this.grid || !this.fade) return;
    this.expanded = false;

    this.fade.querySelector('button')?.addEventListener('click', () => this.expand());
    this.grid.addEventListener('focusin', (event) => {
      const visibleBottom = this.wall.getBoundingClientRect().bottom - (this.fade.hidden ? 0 : this.fade.offsetHeight);
      if (event.target.getBoundingClientRect().bottom > visibleBottom) this.expand();
    });
    this.onBlockSelect = (event) => {
      if (this.contains(event.target)) this.expand();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.onResize = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    };
    window.addEventListener('resize', this.onResize);
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.wall);
    this.cells().forEach((cell) => this.resizeObserver.observe(cell.firstElementChild));
    document.fonts?.ready.then(this.onResize);
    this.layout();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  cells() {
    return [...this.grid.querySelectorAll(':scope > .flux-testimonials__cell')];
  }

  fillCountryList() {
    const list = this.querySelector('[data-country-list]');
    if (!list) return;
    const lang = document.documentElement.lang || 'en';
    const names = new Intl.DisplayNames([lang], { type: 'region' });
    // The long form of HK is "Hong Kong SAR China"
    const shortNames = new Intl.DisplayNames([lang], { type: 'region', style: 'short' });
    const countries = (this.dataset.shipped || '')
      .split(',')
      .filter((code) => /^[A-Z]{2}$/.test(code))
      .map((code) => (code === 'HK' ? shortNames : names).of(code))
      .sort((a, b) => a.localeCompare(b, lang));
    list.textContent = new Intl.ListFormat([lang]).format(countries);
  }

  layout() {
    const style = getComputedStyle(this.grid);
    const cols = parseInt(style.getPropertyValue('--cols'), 10) || 1;
    const gap = parseFloat(style.columnGap) || 0;
    const width = (this.grid.getBoundingClientRect().width - gap * (cols - 1)) / cols;
    const cells = this.cells();
    this.grid.classList.add('is-masonry');

    cells.forEach((cell, i) => {
      cell.style.width = `${width}px`;
      cell.style.left = `${(i % cols) * (width + gap)}px`;
    });
    const heights = cells.map((cell) => cell.getBoundingClientRect().height);
    const bottoms = new Array(cols).fill(0);
    const posts = Array.from({ length: cols }, () => []);
    cells.forEach((cell, i) => {
      const col = i % cols;
      const top = posts[col].length ? bottoms[col] + gap : 0;
      cell.style.top = `${top}px`;
      posts[col].push({ top, height: heights[i] });
      bottoms[col] = top + heights[i];
    });
    const total = Math.max(...bottoms);
    this.grid.style.height = `${total}px`;

    if (this.expanded) return;
    const shown = Math.max(
      ...posts.map((column) => {
        if (column.length < 3) return column.length ? column.at(-1).top + column.at(-1).height : 0;
        return column[2].top + column[2].height / 2;
      })
    );
    const needed = shown < total - 1;
    this.wall.style.maxHeight = needed ? `${Math.round(shown)}px` : '';
    this.fade.hidden = !needed;
  }

  expand() {
    this.expanded = true;
    this.wall.style.maxHeight = '';
    this.fade.hidden = true;
  }
}

if (!customElements.get('flux-testimonials')) customElements.define('flux-testimonials', FluxTestimonials);

document.addEventListener('shopify:block:select', (event) => {
  if (event.target instanceof HTMLDetailsElement) event.target.open = true;
});
